import { useContext, useEffect, useState } from 'react';
import { Field, FieldLabel } from '../../components/field/field';
import { Modal } from '../../components/modal/modal';
import { Typography } from '../../components/typography/typography';
import { toast } from '../../util/toasts';
import { EmailMessage } from '../../clients/quipli-service/email/email';
import { Location } from '../../clients/quipli-service/location/location';
import { TenantContext } from '../../context/tenant-context';
import { Service } from '../../clients/customer-api/cart/cart';
import { DateTime } from 'luxon';
import { useCustomerAPI } from '../../clients/customer-api/customer-api-client';
import { EcommerceProduct } from '../products/product-details/product-details';
import { Button } from '../../components/form/button';
import { OrderType } from '../../clients/quipli-service/order/order';

type OfflineOrderRequest = {
  name?: string;
  email?: string;
  phone?: string;
  message?: string;
};
export const OfflineOrderModal = ({
  isOpen,
  product,
  quantity,
  orderType,
  services,
  rentalStart,
  rentalEnd,
  location,
  close,
}: {
  isOpen: boolean;
  product: EcommerceProduct;
  quantity: number;
  orderType: OrderType;
  services?: Service[];
  rentalStart: DateTime;
  rentalEnd: DateTime;
  location?: Location;
  close: () => void;
}) => {
  const { sendEmailAsync, getMultipleLabels } = useCustomerAPI();
  const { contextPath } = useContext(TenantContext);
  const [orderFormLabel, setOrderFormLabel] = useState<string>('');
  const [offlineOrderRequest, setOfflineOrderRequest] = useState<OfflineOrderRequest>({
    name: '',
    email: '',
    phone: '',
    message: '',
  });

  useEffect(() => {
    const init = async () => {
      const label = await getMultipleLabels(['L_ORDER_FORM_TEXT']);
      setOrderFormLabel(label[0].caption);
    };
    init();
  }, []);

  const sendOfflineOrderEmail = async () => {
    try {
      const message = `${offlineOrderRequest.message}<br/>
            <br><br><b>Product name:</b> <a href="${contextPath}manager/products/view/${product.id}" target="_blank">${
        product.name
      }</a>
            <br>${services ? '<div><p style="padding: 0; margin: 0;"><b>Cargo Requirements:</b></p>' : ''}
            ${(services?.map((s) => `<p style="padding: 0; margin: 0;"><small> ${s.value}</small></p>`) ?? []).join(
              '<br>',
            )}
            ${services ? '</div>' : ''}
            ${
              orderType === OrderType.Rental
                ? `<b>Rental duration:</b> <br>
            <b>From:</b>${rentalStart.toFormat('LLL dd, yyyy hh:mm a')}<br>
            <b>To:</b>${rentalEnd.toFormat('LLL dd, yyyy hh:mm a')}<br>`
                : ''
            }
            <b>Quantity:</b>${quantity}<br>
            <br>
            `;

      const m: EmailMessage = {
        templateName: 'offline_order',
        locationId: location?.id,
        formName: offlineOrderRequest.name,
        formEmail: offlineOrderRequest.email,
        formPhone: offlineOrderRequest.phone,
        formMessage: message,
        formLocationName: location?.name,
      };
      await sendEmailAsync(m);
      toast(
        'Your Order form has been submitted successfully. Our team will get in contact with you soon for confirmation',
        'success',
      );
      close();
    } catch (e) {
      toast('Failed to send order request', 'error');
    }
  };

  return (
    <Modal isOpen={isOpen} close={close} title="Order Form">
      <div className="offline-order-form">
        <Typography className="text-center" type="text-md" weight="semi-bold">
          {orderFormLabel}
        </Typography>

        <Field
          isRequired={true}
          type="text"
          label="Your Name"
          value={offlineOrderRequest.name}
          onChange={(e) => setOfflineOrderRequest({ ...offlineOrderRequest, name: e })}
        />
        <Field
          isRequired={true}
          type="text"
          label="Email Address"
          value={offlineOrderRequest.email}
          onChange={(e) => setOfflineOrderRequest({ ...offlineOrderRequest, email: e })}
        />
        <Field
          isRequired={true}
          type="text"
          label="Phone Number"
          value={offlineOrderRequest.phone}
          onChange={(e) => setOfflineOrderRequest({ ...offlineOrderRequest, phone: e })}
        />

        <FieldLabel label="Order Details" isRequired={true} />
        <textarea
          className="w-full"
          value={offlineOrderRequest.message}
          onChange={(e) => setOfflineOrderRequest({ ...offlineOrderRequest, message: e.target.value })}
        ></textarea>
        <div className="flex justify-end gap-4 mt-4">
          <Button className="btn button-secondary" onClick={() => close()}>
            Cancel
          </Button>
          <Button
            disabled={
              !offlineOrderRequest.email ||
              !offlineOrderRequest.name ||
              !offlineOrderRequest.phone ||
              !offlineOrderRequest.message
            }
            className="btn button-primary"
            onClick={() => sendOfflineOrderEmail()}
          >
            Submit
          </Button>
        </div>
      </div>
    </Modal>
  );
};
